<script setup>
import { h, computed, onMounted } from 'vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import {
  SidebarProvider,
  UNSidebar,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  SidebarRail,
} from './index';
import UNANavMain from './UNANavMain.vue';
import Logo from 'next/icon/Logo.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import SidebarAccountSwitcher from '../sidebar/SidebarAccountSwitcher.vue';
import SidebarProfileMenu from '../sidebar/SidebarProfileMenu.vue';
import SidebarThemeToggle from './SidebarThemeToggle.vue';
import ChannelLeaf from '../sidebar/ChannelLeaf.vue';

const emit = defineEmits(['openKeyShortcutModal', 'showCreateAccountModal']);

const { accountScopedRoute, currentAccount } = useAccount();

const DASHBOARD_SIDEBAR_MODULE_ITEM_NAMES = [
  'Tasks',
  'Drive',
  'Bookmarks',
  'Taxis',
  'Website',
  'Accounting',
];

/** Masqués pour le rôle métier taxis (barre latérale uniquement). */
const TAXIS_AGENT_HIDDEN_MENU_NAMES = new Set([
  'Drive',
  'Bookmarks',
  'Website',
  'Accounting',
  'Campaigns',
  'Portals',
  'ClientPortals',
  'Captain',
]);

const currentRole = useMapGetter('getCurrentRole');

const sidebarModuleFlags = computed(
  () => currentAccount.value?.dashboard_sidebar_modules ?? {}
);
const store = useStore();
const searchShortcut = useKbd([`$mod`, 'k']);
const { t } = useI18n();

const inboxes = useMapGetter('inboxes/getInboxes');
const labels = useMapGetter('labels/getLabelsOnSidebar');
const teams = useMapGetter('teams/getMyTeams');
const contactCustomViews = useMapGetter('customViews/getContactCustomViews');
const conversationCustomViews = useMapGetter(
  'customViews/getConversationCustomViews'
);

onMounted(() => {
  store.dispatch('labels/get');
  store.dispatch('inboxes/get');
  store.dispatch('notifications/unReadCount');
  store.dispatch('teams/get');
  store.dispatch('attributes/get');
  store.dispatch('customViews/get', 'conversation');
  store.dispatch('customViews/get', 'contact');
});

const sortedInboxes = computed(() =>
  inboxes.value.slice().sort((a, b) => a.name.localeCompare(b.name))
);

const newReportRoutes = () => [
  {
    name: 'Reports Agent',
    label: t('SIDEBAR.REPORTS_AGENT'),
    to: accountScopedRoute('agent_reports_index'),
    activeOn: ['agent_reports_show'],
  },
  {
    name: 'Reports Label',
    label: t('SIDEBAR.REPORTS_LABEL'),
    to: accountScopedRoute('label_reports_index'),
  },
  {
    name: 'Reports Inbox',
    label: t('SIDEBAR.REPORTS_INBOX'),
    to: accountScopedRoute('inbox_reports_index'),
    activeOn: ['inbox_reports_show'],
  },
  {
    name: 'Reports Team',
    label: t('SIDEBAR.REPORTS_TEAM'),
    to: accountScopedRoute('team_reports_index'),
    activeOn: ['team_reports_show'],
  },
];

const reportRoutes = computed(() => newReportRoutes());

const menuItems = computed(() => {
  const items = [
    // Home / Accueil
    {
      name: 'Home',
      label: t('SIDEBAR.HOME_PAGE'),
      icon: 'i-lucide-home',
      to: accountScopedRoute('home_index'),
      activeOn: ['home_index'],
      spacingTop: true,
      spacingBottom: true,
    },
    // SECTION: WORKSPACE
    { section: 'WORKSPACE' },
    {
      name: 'Inbox',
      label: t('SIDEBAR.INBOX'),
      icon: 'i-lucide-inbox',
      to: accountScopedRoute('inbox_view'),
      activeOn: ['inbox_view', 'inbox_view_conversation'],
      getterKeys: {
        count: 'notifications/getUnreadCount',
      },
    },
    {
      name: 'Conversation',
      label: t('SIDEBAR.CONVERSATIONS'),
      icon: 'i-lucide-message-circle',
      children: [
        {
          name: 'All',
          label: t('SIDEBAR.ALL_CONVERSATIONS'),
          activeOn: ['inbox_conversation'],
          to: accountScopedRoute('home'),
        },
        {
          name: 'Mentions',
          label: t('SIDEBAR.MENTIONED_CONVERSATIONS'),
          activeOn: ['conversation_through_mentions'],
          to: accountScopedRoute('conversation_mentions'),
        },
        {
          name: 'Unattended',
          activeOn: ['conversation_through_unattended'],
          label: t('SIDEBAR.UNATTENDED_CONVERSATIONS'),
          to: accountScopedRoute('conversation_unattended'),
        },
        {
          name: 'Folders',
          label: t('SIDEBAR.CUSTOM_VIEWS_FOLDER'),
          icon: 'i-lucide-folder',
          activeOn: ['conversations_through_folders'],
          children: conversationCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute('folder_conversations', { id: view.id }),
          })),
        },
        {
          name: 'Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          activeOn: ['conversations_through_team'],
          children: teams.value.map(team => ({
            name: `${team.name}-${team.id}`,
            label: team.name,
            to: accountScopedRoute('team_conversations', { teamId: team.id }),
          })),
        },
        {
          name: 'Channels',
          label: t('SIDEBAR.CHANNELS'),
          icon: 'i-lucide-mailbox',
          activeOn: ['conversation_through_inbox'],
          children: sortedInboxes.value.map(inbox => ({
            name: `${inbox.name}-${inbox.id}`,
            label: inbox.name,
            to: accountScopedRoute('inbox_dashboard', { inbox_id: inbox.id }),
            component: leafProps =>
              h(ChannelLeaf, {
                label: leafProps.label,
                active: leafProps.active,
                inbox,
              }),
          })),
        },
        {
          name: 'Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tag',
          activeOn: ['conversations_through_label'],
          children: labels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            icon: h('span', {
              class: `size-[12px] ring-1 ring-n-alpha-1 dark:ring-white/20 ring-inset rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute('label_conversations', {
              label: label.title,
            }),
          })),
        },
      ],
    },
    {
      name: 'Tasks',
      label: t('SIDEBAR.TASKS'),
      icon: 'i-lucide-kanban-square',
      to: accountScopedRoute('tasks_index'),
      activeOn: ['tasks_index'],
    },
    {
      name: 'Drive',
      label: t('SIDEBAR.DRIVE'),
      icon: 'i-lucide-hard-drive',
      children: [
        {
          name: 'Files',
          label: t('SIDEBAR.DRIVE_FILES'),
          to: accountScopedRoute('drive_index'),
          activeOn: ['drive_index', 'drive_show'],
        },
        {
          name: 'Reports',
          label: t('SIDEBAR.DRIVE_REPORTS'),
          to: accountScopedRoute('drive_reports'),
          activeOn: ['drive_reports'],
        },
        {
          name: 'RAG',
          label: t('SIDEBAR.DRIVE_RAG'),
          to: accountScopedRoute('drive_rag'),
          activeOn: ['drive_rag'],
        },
      ],
    },
    {
      name: 'Bookmarks',
      label: t('SIDEBAR.BOOKMARKS'),
      icon: 'i-lucide-bookmark',
      to: accountScopedRoute('bookmarks_index'),
      activeOn: ['bookmarks_index'],
    },
    {
      name: 'Taxis',
      label: t('SIDEBAR.TAXIS'),
      icon: 'i-lucide-car',
      children: [
        {
          name: 'Taxis Chauffeurs',
          label: t('SIDEBAR.TAXIS_DRIVERS'),
          to: accountScopedRoute('taxis_chauffeurs_index'),
          activeOn: ['taxis_chauffeurs_index'],
        },
        {
          name: 'Taxis Clients',
          label: t('SIDEBAR.TAXIS_CLIENTS'),
          to: accountScopedRoute('taxis_clients_index'),
          activeOn: ['taxis_clients_index'],
        },
        {
          name: 'Taxis Partenaires',
          label: t('SIDEBAR.TAXIS_PARTNERS'),
          to: accountScopedRoute('taxis_partners_index'),
          activeOn: ['taxis_partners_index'],
        },
        {
          name: 'Taxis Historique',
          label: t('SIDEBAR.TAXIS_HISTORY'),
          to: accountScopedRoute('taxis_agenda_index'),
          activeOn: ['taxis_agenda_index'],
        },
        {
          name: 'Taxis Automation n8n',
          label: t('SIDEBAR.TAXIS_N8N_AUTOMATION'),
          to: accountScopedRoute('taxis_automation_index'),
          activeOn: ['taxis_automation_index'],
        },
        {
          name: 'Taxis Zones',
          label: t('SIDEBAR.TAXIS_ZONES'),
          to: accountScopedRoute('taxis_zones_index'),
          activeOn: ['taxis_zones_index'],
        },
      ],
    },
    {
      name: 'Website',
      label: t('SIDEBAR.WEBSITE'),
      icon: 'i-lucide-globe',
      children: [
        {
          name: 'CMS',
          label: t('SIDEBAR.WEBSITE_CMS'),
          to: accountScopedRoute('website_cms_index'),
          activeOn: ['website_cms_index'],
        },
        {
          name: 'Theme',
          label: t('SIDEBAR.WEBSITE_THEME'),
          to: accountScopedRoute('website_theme_index'),
          activeOn: ['website_theme_index'],
        },
        {
          name: 'Analytics',
          label: t('SIDEBAR.WEBSITE_ANALYTICS'),
          to: accountScopedRoute('website_analytics_index'),
          activeOn: ['website_analytics_index'],
        },
        {
          name: 'Domain',
          label: t('SIDEBAR.WEBSITE_DOMAIN'),
          to: accountScopedRoute('website_domain_index'),
          activeOn: ['website_domain_index'],
        },
        {
          name: 'SEO',
          label: t('SIDEBAR.WEBSITE_SEO'),
          to: accountScopedRoute('website_seo_index'),
          activeOn: ['website_seo_index'],
        },
        {
          name: 'Products',
          label: t('SIDEBAR.WEBSITE_PRODUCTS'),
          to: accountScopedRoute('website_products_index'),
          activeOn: ['website_products_index'],
        },
        {
          name: 'ProductCategories',
          label: t('SIDEBAR.WEBSITE_PRODUCT_CATEGORIES'),
          to: accountScopedRoute('website_product_categories_index'),
          activeOn: ['website_product_categories_index'],
        },
        {
          name: 'Manufacturers',
          label: t('SIDEBAR.WEBSITE_MANUFACTURERS'),
          to: accountScopedRoute('website_manufacturers_index'),
          activeOn: ['website_manufacturers_index'],
        },
        {
          name: 'Shops',
          label: t('SIDEBAR.WEBSITE_SHOPS'),
          to: accountScopedRoute('website_shops_index'),
          activeOn: ['website_shops_index'],
        },
        {
          name: 'Orders',
          label: t('SIDEBAR.WEBSITE_ORDERS'),
          to: accountScopedRoute('website_orders_index'),
          activeOn: ['website_orders_index'],
        },
        {
          name: 'Blog',
          label: t('SIDEBAR.WEBSITE_BLOG'),
          to: accountScopedRoute('website_blog_index'),
          activeOn: ['website_blog_index'],
        },
        {
          name: 'Forms',
          label: t('SIDEBAR.WEBSITE_FORMS'),
          to: accountScopedRoute('website_forms_index'),
          activeOn: ['website_forms_index'],
        },
      ],
    },
    {
      name: 'Accounting',
      label: t('SIDEBAR.ACCOUNTING'),
      icon: 'i-lucide-receipt',
      children: [
        {
          name: 'Invoice Management',
          label: t('SIDEBAR.ACCOUNTING_INVOICES'),
          to: accountScopedRoute('accounting_invoices'),
          activeOn: ['accounting_invoices'],
        },
      ],
    },

    // SECTION: CLIENTS
    { section: 'CLIENTS' },
    {
      name: 'Contacts',
      label: t('SIDEBAR.CONTACTS'),
      icon: 'i-lucide-contact',
      children: [
        {
          name: 'All Contacts',
          label: t('SIDEBAR.ALL_CONTACTS'),
          to: accountScopedRoute(
            'contacts_dashboard_index',
            {},
            { page: 1, search: undefined }
          ),
          activeOn: ['contacts_dashboard_index', 'contacts_edit'],
        },
        {
          name: 'Active',
          label: t('SIDEBAR.ACTIVE'),
          to: accountScopedRoute('contacts_dashboard_active'),
          activeOn: ['contacts_dashboard_active'],
        },
        {
          name: 'Segments',
          icon: 'i-lucide-group',
          label: t('SIDEBAR.CUSTOM_VIEWS_SEGMENTS'),
          children: contactCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute(
              'contacts_dashboard_segments_index',
              { segmentId: view.id },
              { page: 1 }
            ),
            activeOn: [
              'contacts_dashboard_segments_index',
              'contacts_edit_segment',
            ],
          })),
        },
        {
          name: 'Tagged With',
          icon: 'i-lucide-tag',
          label: t('SIDEBAR.TAGGED_WITH'),
          children: labels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            icon: h('span', {
              class: `size-[12px] ring-1 ring-n-alpha-1 dark:ring-white/20 ring-inset rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute(
              'contacts_dashboard_labels_index',
              { label: label.title },
              { page: 1, search: undefined }
            ),
            activeOn: [
              'contacts_dashboard_labels_index',
              'contacts_edit_label',
            ],
          })),
        },
      ],
    },

    // SECTION: ANALYTICS
    { section: 'ANALYTICS' },
    {
      name: 'Reports',
      label: t('SIDEBAR.REPORTS'),
      icon: 'i-lucide-chart-spline',
      children: [
        {
          name: 'Report Overview',
          label: t('SIDEBAR.REPORTS_OVERVIEW'),
          to: accountScopedRoute('account_overview_reports'),
        },
        {
          name: 'Report Conversation',
          label: t('SIDEBAR.REPORTS_CONVERSATION'),
          to: accountScopedRoute('conversation_reports'),
        },
        ...reportRoutes.value,
        {
          name: 'Reports CSAT',
          label: t('SIDEBAR.CSAT'),
          to: accountScopedRoute('csat_reports'),
        },
        {
          name: 'Reports SLA',
          label: t('SIDEBAR.REPORTS_SLA'),
          to: accountScopedRoute('sla_reports'),
        },
        {
          name: 'Reports Bot',
          label: t('SIDEBAR.REPORTS_BOT'),
          to: accountScopedRoute('bot_reports'),
        },
      ],
    },

    // SECTION: MARKETING
    { section: 'MARKETING' },
    {
      name: 'Campaigns',
      label: t('SIDEBAR.CAMPAIGNS'),
      icon: 'i-lucide-megaphone',
      children: [
        {
          name: 'Live chat',
          label: t('SIDEBAR.LIVE_CHAT'),
          to: accountScopedRoute('campaigns_livechat_index'),
        },
        {
          name: 'SMS',
          label: t('SIDEBAR.SMS'),
          to: accountScopedRoute('campaigns_sms_index'),
        },
        {
          name: 'WhatsApp',
          label: t('SIDEBAR.WHATSAPP'),
          to: accountScopedRoute('campaigns_whatsapp_index'),
        },
        {
          name: 'Email',
          label: t('SIDEBAR.EMAIL'),
          to: accountScopedRoute('campaigns_email_index'),
        },
      ],
    },
    {
      name: 'Portals',
      label: t('SIDEBAR.HELP_CENTER.TITLE'),
      icon: 'i-lucide-library-big',
      children: [
        {
          name: 'Articles',
          label: t('SIDEBAR.HELP_CENTER.ARTICLES'),
          activeOn: [
            'portals_articles_index',
            'portals_articles_new',
            'portals_articles_edit',
          ],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_articles_index',
          }),
        },
        {
          name: 'Categories',
          label: t('SIDEBAR.HELP_CENTER.CATEGORIES'),
          activeOn: [
            'portals_categories_index',
            'portals_categories_articles_index',
            'portals_categories_articles_edit',
          ],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_categories_index',
          }),
        },
        {
          name: 'Locales',
          label: t('SIDEBAR.HELP_CENTER.LOCALES'),
          activeOn: ['portals_locales_index'],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_locales_index',
          }),
        },
        {
          name: 'Settings',
          label: t('SIDEBAR.HELP_CENTER.SETTINGS'),
          activeOn: ['portals_settings_index'],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_settings_index',
          }),
        },
      ],
    },
    {
      name: 'ClientPortals',
      label: t('SIDEBAR.CLIENT_PORTALS'),
      icon: 'i-lucide-layout-dashboard',
      to: accountScopedRoute('client_portals_index'),
      activeOn: ['client_portals_index'],
    },

    // SECTION: CONFIGURATION
    { section: 'CONFIGURATION' },
    {
      name: 'Captain',
      icon: 'i-woot-captain',
      label: t('SIDEBAR.CAPTAIN'),
      children: [
        {
          name: 'Assistants',
          label: t('SIDEBAR.CAPTAIN_ASSISTANTS'),
          to: accountScopedRoute('captain_assistants_index'),
        },
        {
          name: 'Documents',
          label: t('SIDEBAR.CAPTAIN_DOCUMENTS'),
          to: accountScopedRoute('captain_documents_index'),
        },
        {
          name: 'Responses',
          label: t('SIDEBAR.CAPTAIN_RESPONSES'),
          to: accountScopedRoute('captain_responses_index'),
        },
        {
          name: 'Tools',
          label: t('SIDEBAR.CAPTAIN_TOOLS'),
          to: accountScopedRoute('captain_tools_index'),
        },
      ],
    },
    {
      name: 'Settings',
      label: t('SIDEBAR.SETTINGS'),
      icon: 'i-lucide-bolt',
      children: [
        {
          name: 'Settings Account Settings',
          label: t('SIDEBAR.ACCOUNT_SETTINGS'),
          icon: 'i-lucide-briefcase',
          to: accountScopedRoute('general_settings_index'),
        },
        {
          name: 'Settings Agents',
          label: t('SIDEBAR.AGENTS'),
          icon: 'i-lucide-square-user',
          to: accountScopedRoute('agent_list'),
        },
        {
          name: 'Settings Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          to: accountScopedRoute('settings_teams_list'),
        },
        {
          name: 'Settings Agent Assignment',
          label: t('SIDEBAR.AGENT_ASSIGNMENT'),
          icon: 'i-lucide-user-cog',
          to: accountScopedRoute('assignment_policy_index'),
        },
        {
          name: 'Settings Inboxes',
          label: t('SIDEBAR.INBOXES'),
          icon: 'i-lucide-inbox',
          to: accountScopedRoute('settings_inbox_list'),
        },
        {
          name: 'Settings Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tags',
          to: accountScopedRoute('labels_list'),
        },
        {
          name: 'Settings Custom Attributes',
          label: t('SIDEBAR.CUSTOM_ATTRIBUTES'),
          icon: 'i-lucide-code',
          to: accountScopedRoute('attributes_list'),
        },
        {
          name: 'Settings Automation',
          label: t('SIDEBAR.AUTOMATION'),
          icon: 'i-lucide-workflow',
          to: accountScopedRoute('automation_list'),
        },
        {
          name: 'Settings Agent Bots',
          label: t('SIDEBAR.AGENT_BOTS'),
          icon: 'i-lucide-bot',
          to: accountScopedRoute('agent_bots'),
        },
        {
          name: 'Settings Macros',
          label: t('SIDEBAR.MACROS'),
          icon: 'i-lucide-toy-brick',
          to: accountScopedRoute('macros_wrapper'),
        },
        {
          name: 'Settings Canned Responses',
          label: t('SIDEBAR.CANNED_RESPONSES'),
          icon: 'i-lucide-message-square-quote',
          to: accountScopedRoute('canned_list'),
        },
        {
          name: 'Settings Integrations',
          label: t('SIDEBAR.INTEGRATIONS'),
          icon: 'i-lucide-blocks',
          to: accountScopedRoute('settings_applications'),
        },
        {
          name: 'Settings Audit Logs',
          label: t('SIDEBAR.AUDIT_LOGS'),
          icon: 'i-lucide-briefcase',
          to: accountScopedRoute('auditlogs_list'),
        },
        {
          name: 'Settings Custom Roles',
          label: t('SIDEBAR.CUSTOM_ROLES'),
          icon: 'i-lucide-shield-plus',
          to: accountScopedRoute('custom_roles_list'),
        },
        {
          name: 'Settings Sla',
          label: t('SIDEBAR.SLA'),
          icon: 'i-lucide-clock-alert',
          to: accountScopedRoute('sla_list'),
        },
        {
          name: 'Settings Security',
          label: t('SIDEBAR.SECURITY'),
          icon: 'i-lucide-shield',
          to: accountScopedRoute('security_settings_index'),
        },
        {
          name: 'Settings Billing',
          label: t('SIDEBAR.BILLING'),
          icon: 'i-lucide-credit-card',
          to: accountScopedRoute('billing_settings_index'),
        },
      ],
    },
  ];

  return items.filter(item => {
    if (currentRole.value === 'taxis_agent') {
      if (item.section === 'MARKETING') return false;
      if (item.name && TAXIS_AGENT_HIDDEN_MENU_NAMES.has(item.name))
        return false;
    }

    if (item.section) return true;
    if (!DASHBOARD_SIDEBAR_MODULE_ITEM_NAMES.includes(item.name)) return true;
    const key = item.name.toLowerCase();
    return sidebarModuleFlags.value[key] !== false;
  });
});
</script>

<template>
  <SidebarProvider default-open>
    <UNSidebar collapsible="icon" rail>
      <SidebarHeader
        class="grid gap-3.5 pt-3 pb-4 px-2.5 border-b border-n-sidebar-border group-data-[collapsible=icon]:hidden"
      >
        <!-- Logo + Account Switcher -->
        <div class="flex items-center min-w-0 gap-3">
          <div class="grid flex-shrink-0 size-7 place-content-center">
            <Logo class="size-5" />
          </div>
          <div class="flex-shrink-0 w-px h-4 bg-n-slate-6" />
          <SidebarAccountSwitcher
            class="flex-grow min-w-0 -mx-1"
            @show-create-account-modal="emit('showCreateAccountModal')"
          />
        </div>

        <!-- Barre de recherche et bouton composer -->
        <div class="flex gap-2">
          <RouterLink
            :to="{ name: 'search' }"
            class="flex items-center w-full gap-2.5 px-3 py-2 rounded-lg h-9 outline outline-1 outline-n-slate-5 bg-white hover:bg-n-sidebar-item-hover hover:outline-n-sidebar-indicator group will-change-auto transition-all duration-200 ease-out"
          >
            <span
              class="flex-shrink-0 i-lucide-search size-[18px] text-n-sidebar-indicator transition-colors duration-200"
            />
            <span
              class="flex-grow text-left text-n-sidebar-text text-[15px] transition-colors duration-200"
            >
              {{ t('COMBOBOX.SEARCH_PLACEHOLDER') }}
            </span>
            <span
              class="hidden tracking-wide pointer-events-none select-none text-n-slate-10 text-xs"
            >
              {{ searchShortcut }}
            </span>
          </RouterLink>
          <ComposeConversation align-position="right">
            <template #trigger="{ toggle }">
              <Button
                icon="i-lucide-pen-line"
                color="slate"
                size="sm"
                class="!h-9 !w-9 !bg-white !outline-1 !outline-n-slate-5 !text-n-sidebar-indicator hover:!bg-n-sidebar-item-hover hover:!outline-n-sidebar-indicator !transition-all !duration-200 !ease-out"
                @click="toggle"
              />
            </template>
          </ComposeConversation>
        </div>
      </SidebarHeader>

      <SidebarContent
        class="grid flex-grow gap-2.5 px-2.5 pb-4 overflow-y-scroll no-scrollbar"
      >
        <UNANavMain :items="menuItems" />

        <!-- Theme Toggle au-dessus de la bordure -->
        <div class="mt-auto pt-2">
          <SidebarThemeToggle />
        </div>
      </SidebarContent>

      <SidebarFooter
        class="p-2.5 border-t border-n-sidebar-border flex-shrink-0 flex justify-between gap-2.5 items-center transition-all duration-200 ease-out group-data-[collapsible=icon]:hidden"
      >
        <!-- Profile Menu -->
        <SidebarProfileMenu
          @open-key-shortcut-modal="emit('openKeyShortcutModal')"
        />
      </SidebarFooter>

      <SidebarRail />
    </UNSidebar>

    <!-- Slot pour le contenu principal -->
    <slot />
  </SidebarProvider>
</template>
